import { AuthRequest, AuthResponse, GrantType, AuthMfaRequest, MfaResponse, PasswordExpiredResponse } from './auth';
import { LoginStartResponse } from './login-flow.models';
import { Observable, of, Subject } from 'rxjs';
import { Token, TokenPayload } from './token';
import { catchError, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Permissions } from '../models/permissions';
import { Roles } from '../models/roles';
import { StorageService } from '../storage/storage.service';
import { ForgotPasswordResponse, User } from '../models/user';
import { environment } from '../environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '../store/states/app.state';
import { selectTenant } from '../store/selectors/tenant.selector';
import { BaseService } from '../base/base-service';
import { logAuthError, logAuthWarn } from './auth-debug.helpers';

export const AUTH_TOKEN_STORAGE_KEY = 'token';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {
  private readonly tokenSyncSubject = new Subject<'updated' | 'cleared'>();
  /** Emits when token is saved or cleared (same tab or after sync). */
  readonly tokenSync$ = this.tokenSyncSubject.asObservable();

  constructor(
    http: HttpClient,
    private readonly storageService: StorageService,
    store: Store<AppState>
  ) {
    super(http, store);
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', event => this.onStorageEvent(event));
    }
  }

  private onStorageEvent(event: StorageEvent): void {
    if (event.key !== AUTH_TOKEN_STORAGE_KEY) {
      return;
    }
    if (event.newValue) {
      try {
        this._token = JSON.parse(event.newValue) as Token;
        this.applyTokenOffset(this._token);
        this.tokenSyncSubject.next('updated');
      } catch (e) {
        logAuthError('storage sync: failed to parse token', e);
      }
      return;
    }
    this._token = null;
    this.offset = 0;
    this.tokenSyncSubject.next('cleared');
  }

  readonly uris = {
    main: 'oauth/token',
    me: `api/${environment.api.version}/users/me`,
    roles: `api/${environment.api.version}/roles`,
    permissions: `api/${environment.api.version}/permissions`,
    forgotPassword:  `api/${environment.api.version}/passwordreset`,
    validatePasswordReset: `api/${environment.api.version}/passwordreset/validate`,
    loginStart: `api/${environment.api.version}/auth/login`,
    loginMfa: `api/${environment.api.version}/auth/login/mfa`,
    /** @deprecated */
    verifymfa: `api/${environment.api.version}/2fa/verify`,
    /** @deprecated */
    enabledmfa: `api/${environment.api.version}/2fa/enable`,
    updatePasswordExpired: `api/${environment.api.version}/update-password-expired`,
  };

  private _token: Token = null;
  private offset: number = 0;

  /** Read latest token from localStorage into memory (multi-tab safe). */
  syncTokenFromStorage(): Token | null {
    const stored = this.storageService.get(AUTH_TOKEN_STORAGE_KEY) as Token | null;
    if (!stored?.access_token) {
      this._token = null;
      this.offset = 0;
      return null;
    }
    this._token = stored;
    this.applyTokenOffset(this._token);
    return this._token;
  }

  private applyTokenOffset(token: Token): void {
    const payload = this.getTokenPayload(token);
    if (payload?.iat) {
      this.offset = Math.ceil(new Date().getTime() / 1000) - payload.iat;
    }
  }

  init(): { token: Token } {
    this.syncTokenFromStorage();
    if (!this._token) {
      return { token: null };
    }
    // Keep session when access JWT expired but refresh_token is still present.
    if (!this.isTokenValid(this._token) && !this.hasRefreshToken(this._token)) {
      this.clearToken('init: stored token invalid and no refresh_token');
    }
    return { token: this._token };
  }

  /**
   * Step 1 — Validate email + password; returns MFA challenge (no session yet).
   */
  startLogin(
    email: string,
    password: string,
    captchaToken: string
  ): Observable<LoginStartResponse> {
    return this.http.post<LoginStartResponse>(
      this.getUrl(this.uris.loginStart, false),
      { email, password, token: captchaToken }
    );
  }

  /**
   * Step 2 — Verify TOTP with challenge from startLogin (password not sent again).
   */
  completeLoginMfa(
    challenge: string,
    code: string,
    captchaToken: string
  ): Observable<MfaResponse> {
    return this.http.post<MfaResponse>(this.getUrl(this.uris.loginMfa, false), {
      challenge,
      code,
      token: captchaToken
    });
  }

  /** @deprecated Use completeLoginMfa */
  verifyTwoFactor(
    code: string,
    email: string,
    password: string,
    captchaToken: string
  ): Observable<MfaResponse> {
    return this.http.post<MfaResponse>(this.getUrl(this.uris.verifymfa, false), {
      code,
      user_id: email,
      pass: password,
      token: captchaToken
    });
  }

  /** @deprecated Use verifyTwoFactor */
  verifyMfa(
    code: string = '',
    email: string = '',
    password: string = '',
    token: string = ''
  ): Observable<MfaResponse> {
    return this.verifyTwoFactor(code, email, password, token);
  }

  /** @deprecated Use startLogin(email, password, captchaToken) */
  checkTwoFactorStatus(email: string, captchaToken: string): Observable<MfaResponse> {
    return this.http.post<MfaResponse>(this.getUrl(this.uris.enabledmfa, false), {
      email,
      token: captchaToken
    });
  }

  /** @deprecated Use startLogin */
  enabledMfa(email: string = '', token: string = ''): Observable<MfaResponse> {
    return this.checkTwoFactorStatus(email, token);
  }

  updatePassword(username: string = "", password: string = "", newpassword: string = "", token: string = ""): Observable<PasswordExpiredResponse> {
    return this.http
      .post<PasswordExpiredResponse>(this.getUrl(`${this.uris.updatePasswordExpired}`, false), {
        username: username,
        password: password,
        newpassword: newpassword,
        token: token
      });
  }

  sendRequest(request: AuthRequest = {}): Observable<AuthResponse> { 
    return this.http
      .post<AuthResponse>(this.getUrl(`${this.uris.main}`, false), {
        ...request,
        scope: '*' || request.scope,
        grant_type:
          request.grant_type !== GrantType.RefreshToken
            ? request.username && request.password
              ? GrantType.Password
              : GrantType.ClientCredentials
            : request.grant_type,
        client_id: request.client_id || environment.authentication.client_id,
        client_secret:
          request.client_secret || environment.authentication.client_secret
      })
      .pipe(
        switchMap(loginResponse => {
          if (!loginResponse || !(loginResponse as AuthResponse).access_token) {
            logAuthWarn('oauth/token: response without access_token', {
              grant_type: request.grant_type,
              hasResponse: !!loginResponse
            });
            this.clearToken('oauth/token: response without access_token');
            return of(null as any);
          }
          this._token = { ...this._token, ...loginResponse };
          this.storageService.set(AUTH_TOKEN_STORAGE_KEY, this._token);
          this.applyTokenOffset(this._token);
          this.tokenSyncSubject.next('updated');
          return of(loginResponse);
        }),
        catchError((e) => {
          // Refresh failed: clear so public map catalog requests run as guests
          logAuthError('oauth/token: request failed', e, {
            grant_type: request.grant_type
          });
          this.clearToken('oauth/token: request failed');
          return of(null as any);
        })
      );
  }

  forgotPassword(request: AuthRequest = {}): Observable<ForgotPasswordResponse> {
    return this.http
    .post<ForgotPasswordResponse>(this.getUrl(`${this.uris.forgotPassword}`, false), {
      ...request
    })
    .pipe(
      switchMap(loginResponse => {
        return  of(loginResponse);
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 0) {
          return of(null);
        }
        throw err;
      })
    );
  }

  validatePasswordReset(token: string): Observable<{
    success?: boolean;
    mensaje?: string;
    error?: { message?: string };
  }> {
    return this.http
      .get<{
        success?: boolean;
        mensaje?: string;
        error?: { message?: string };
      }>(this.getUrl(`${this.uris.validatePasswordReset}/${encodeURIComponent(token)}`, false))
      .pipe(
        catchError(err => {
          if (err instanceof HttpErrorResponse && err.status === 0) {
            return of({
              success: false,
              mensaje:
                'El servicio no está disponible, inténtelo de nuevo o más tarde.'
            });
          }
          throw err;
        })
      );
  }

  me(): Observable<User> {
    return this.http.get<any>(this.getUrl(`${this.uris.me}`, false)).pipe(
      catchError(err => {
        return this.cacheHandler(err, 'user');
      })
    );
  }

  roles(): Observable<Roles> {
    return this.http
      .get<Roles>(this.getUrl(`${this.uris.roles}`, false))
      .pipe(
        catchError(err => {
          return this.cacheHandler(err, 'roles');
        })
      );
  }

  permissions(): Observable<Permissions> {
    return this.http
      .get<Permissions>(this.getUrl(`${this.uris.permissions}`, false))
      .pipe(
        catchError(err => {
          return this.cacheHandler(err, 'permissions');
        })
      );
  }

  token(): Observable<Token> {
    this.syncTokenFromStorage();
    if (this.isTokenValid(this._token)) {
      return of(this._token);
    }
    if (this.hasRefreshToken(this._token)) {
      return this.sendRequest({
        client_id: environment.authentication.client_id,
        client_secret: environment.authentication.client_secret,
        grant_type: GrantType.RefreshToken,
        refresh_token: this._token.refresh_token
      });
    }
    return of(null);
  }

  getTokenPayload(token: Token): TokenPayload {
    if (!token?.access_token) {
      return null;
    }
    try {
      const [, payload] = token.access_token.split('.');
      return JSON.parse(atob(payload));
    } catch (e) {
      logAuthError('getTokenPayload: failed to parse JWT', e);
      return null;
    }
  }

  isTokenValid(token: Token): boolean {
    const payload = this.getTokenPayload(token);
    if (!payload) return false;
    const now = this.getAuthNowSeconds();
    return payload.nbf <= now && now < payload.exp;
  }

  /** Seconds until access token expiry (negative if already expired). */
  getAccessTokenSecondsRemaining(token?: Token | null): number | null {
    const activeToken = token ?? this.syncTokenFromStorage();
    const payload = this.getTokenPayload(activeToken);
    if (!payload?.exp) {
      return null;
    }
    return payload.exp - this.getAuthNowSeconds();
  }

  getAuthNowSeconds(): number {
    return Math.ceil(new Date().getTime() / 1000) + this.offset;
  }

  hasRefreshToken(token: Token): boolean {
    if (token && token.refresh_token) return true;
    return false;
  }

  clearToken(reason?: string) {
    if (reason) {
      logAuthWarn('clearToken', { reason });
    }
    this._token = null;
    this.offset = 0;
    this.storageService.remove(AUTH_TOKEN_STORAGE_KEY);
    this.tokenSyncSubject.next('cleared');
  }

  refreshToken(): Observable<Token> {
    this.syncTokenFromStorage();
    if (!this.hasRefreshToken(this._token)) {
      logAuthWarn('refreshToken: no refresh_token available');
      return of(null);
    }

    return this.sendRequest({
      client_id: environment.authentication.client_id,
      client_secret: environment.authentication.client_secret,
      grant_type: GrantType.RefreshToken,
      refresh_token: this._token.refresh_token
    });
  }

  cacheHandler(err: Error, _key: string) {
    if (err instanceof HttpErrorResponse && err.status === 0) {
      return of(null);
    }
    return of(null);
  }
}
